/**
 * Submissions API
 *
 * API functions for UPC submission operations.
 * Types are derived from the generated OpenAPI contract.
 */

import { apiClient } from './client';
import type { ProductDocumentsResponse, SubmissionSource } from './types';
import type {
  Submission,
  SubmissionAttachment,
  SubmissionListResponse,
  SubmissionCreate,
  ReviewSubmissionRequest,
  AddCommentRequest,
  UPCLookupResponse,
  UploadImagesResponse,
  BulkUploadPreview,
  BulkUploadPreviewJobProgress,
  BulkUploadPreviewJobStartResponse,
  BulkUploadCreateRequest,
  BulkUploadCreateResponse,
  BulkImageUploadApplyResponse,
  BulkImageUploadPreview,
} from './generated/contract-types';

export interface AdminEditField {
  field: string;
  value?: string | number | null;
}

export interface AdminEditSubmissionRequest {
  edits: AdminEditField[];
}

export const lookupUPC = async (upc: string): Promise<UPCLookupResponse> => {
  const response = await apiClient.get<UPCLookupResponse>('/products/upc-lookup', {
    params: { upc },
  });
  return response.data;
};

export const getSubmissions = async (
  status?: string,
  page: number = 1,
  perPage: number = 50,
  source?: SubmissionSource
): Promise<SubmissionListResponse> => {
  const response = await apiClient.get<SubmissionListResponse>('/products/submissions', {
    params: {
      status,
      page,
      perPage: perPage,
      source,
    },
  });
  return response.data;
};

export const getSubmission = async (id: number): Promise<Submission> => {
  const response = await apiClient.get<Submission>(`/products/submissions/${id}`);
  return response.data;
};

export const createSubmission = async (data: SubmissionCreate): Promise<Submission> => {
  const response = await apiClient.post<Submission>('/products/submissions', data);
  return response.data;
};

export const reviewSubmission = async (
  id: number,
  request: ReviewSubmissionRequest
): Promise<Submission> => {
  const response = await apiClient.post<Submission>(`/products/submissions/${id}/review`, request);
  return response.data;
};

export const addComment = async (id: number, request: AddCommentRequest): Promise<Submission> => {
  const response = await apiClient.post<Submission>(`/products/submissions/${id}/comment`, request);
  return response.data;
};

export const uploadImages = async (
  submissionId: number,
  files: File[],
  imageTypes: Array<'front' | 'side' | 'top'>,
  sideIsSameAsFront: boolean = false,
  topIsSameAsFront: boolean = false
): Promise<UploadImagesResponse> => {
  const formData = new FormData();

  files.forEach((file) => {
    formData.append('images', file);
  });

  formData.append('image_types', JSON.stringify(imageTypes));
  formData.append('side_is_same_as_front', String(sideIsSameAsFront));
  formData.append('top_is_same_as_front', String(topIsSameAsFront));

  const response = await apiClient.post<UploadImagesResponse>(
    `/products/submissions/${submissionId}/images`,
    formData
  );
  return response.data;
};

export const updateSubmissionImageType = async (
  submissionId: number,
  imageId: number,
  imageType: 'front' | 'side' | 'top'
): Promise<Submission> => {
  const response = await apiClient.patch<Submission>(
    `/products/submissions/${submissionId}/images/${imageId}/type`,
    { imageType }
  );
  return response.data;
};

export interface SubmissionCropBox {
  x: number;
  y: number;
  width: number;
  height: number;
}

export const adjustSubmissionImageCrop = async (
  submissionId: number,
  imageId: number,
  cropBox: SubmissionCropBox
): Promise<Submission> => {
  const response = await apiClient.patch<Submission>(
    `/products/submissions/${submissionId}/images/${imageId}/crop`,
    { cropBox }
  );
  return response.data;
};

export const downloadBulkTemplate = async (): Promise<Blob> => {
  const response = await apiClient.get('/products/submissions/bulk/template', {
    responseType: 'blob',
  });
  return response.data as Blob;
};

export const previewBulkUpload = async (file: File): Promise<BulkUploadPreview> => {
  const formData = new FormData();
  formData.append('file', file);

  const response = await apiClient.post<BulkUploadPreview>(
    '/products/submissions/bulk/upload',
    formData
  );
  return response.data;
};

export const startPreviewBulkUploadJob = async (
  file: File
): Promise<BulkUploadPreviewJobStartResponse> => {
  const formData = new FormData();
  formData.append('file', file);

  const response = await apiClient.post<BulkUploadPreviewJobStartResponse>(
    '/products/submissions/bulk/upload/async',
    formData
  );
  return response.data;
};

export const getPreviewBulkUploadJobProgress = async (
  jobId: number
): Promise<BulkUploadPreviewJobProgress> => {
  const response = await apiClient.get<BulkUploadPreviewJobProgress>(
    `/products/submissions/bulk/upload/jobs/${jobId}/progress`
  );
  return response.data;
};

export const getPreviewBulkUploadJobResult = async (jobId: number): Promise<BulkUploadPreview> => {
  const response = await apiClient.get<BulkUploadPreview>(
    `/products/submissions/bulk/upload/jobs/${jobId}/result`
  );
  return response.data;
};

export const createBulkUpload = async (
  request: BulkUploadCreateRequest
): Promise<BulkUploadCreateResponse> => {
  const response = await apiClient.post<BulkUploadCreateResponse>(
    '/products/submissions/bulk/create',
    request
  );
  return response.data;
};

export const previewBulkImageUpload = async (files: File[]): Promise<BulkImageUploadPreview> => {
  const formData = new FormData();
  files.forEach((file) => {
    formData.append('images', file);
  });

  const response = await apiClient.post<BulkImageUploadPreview>(
    '/products/submissions/bulk/images/preview',
    formData
  );
  return response.data;
};

export const applyBulkImageUpload = async (
  files: File[],
  selectedRows: number[]
): Promise<BulkImageUploadApplyResponse> => {
  const formData = new FormData();
  files.forEach((file) => {
    formData.append('images', file);
  });
  formData.append('selected_rows', JSON.stringify(selectedRows));

  const response = await apiClient.post<BulkImageUploadApplyResponse>(
    '/products/submissions/bulk/images/apply',
    formData
  );
  return response.data;
};

export interface UploadAttachmentsResponse {
  uploadedCount: number;
  attachments: Array<{
    id: number;
    filename: string;
    mimeType: string;
    sizeBytes: number;
    uploadedAt: string;
  }>;
}

export const uploadAttachments = async (
  submissionId: number,
  files: File[]
): Promise<UploadAttachmentsResponse> => {
  const formData = new FormData();
  files.forEach((file) => {
    formData.append('attachments', file);
  });
  const response = await apiClient.post<UploadAttachmentsResponse>(
    `/products/submissions/${submissionId}/attachments`,
    formData
  );
  return response.data;
};

export const adminEditSubmission = async (
  id: number,
  request: AdminEditSubmissionRequest
): Promise<Submission> => {
  const response = await apiClient.patch<Submission>(
    `/products/submissions/${id}/admin-edit`,
    request
  );
  return response.data;
};

export const setNeedsInfo = async (id: number, message: string): Promise<Submission> => {
  const response = await apiClient.post<Submission>(`/products/submissions/${id}/needs-info`, {
    message,
  });
  return response.data;
};

export const submitterReply = async (id: number, message: string): Promise<Submission> => {
  const response = await apiClient.post<Submission>(`/products/submissions/${id}/reply`, {
    message,
  });
  return response.data;
};

export const deleteAttachment = async (
  submissionId: number,
  attachmentId: number
): Promise<{ deleted: boolean }> => {
  const response = await apiClient.delete<{ deleted: boolean }>(
    `/products/submissions/${submissionId}/attachments/${attachmentId}`
  );
  return response.data;
};

export const replaceAttachment = async (
  submissionId: number,
  attachmentId: number,
  file: File
): Promise<SubmissionAttachment> => {
  const formData = new FormData();
  formData.append('attachment', file);
  const response = await apiClient.put<SubmissionAttachment>(
    `/products/submissions/${submissionId}/attachments/${attachmentId}`,
    formData
  );
  return response.data;
};

export const getProductDocuments = async (productId: number): Promise<ProductDocumentsResponse> => {
  const response = await apiClient.get<ProductDocumentsResponse>(
    `/products/submissions/by-product/${productId}/documents`
  );
  return response.data;
};
